import i18n from 'app/i18n';
import { DIALOGS } from './chat-screen-state';
import { Sentry } from '../../entry-setup/sentry';

export const CHAT_STATUS_CUSTOM_MAX_LENGTH = 20;

/**
 * The current user's chat status + the "Set your status" picker. Merged into
 * chatOverlay / chatFullPage. `chatStatus`
 * ({ preset, label, customMessage, customMessageActive }) is seeded from a
 * bootstrap attribute on the component root; preset options are rendered
 * server-side with translated labels. A preset and a custom message are stored
 * independently — `customMessageActive` decides which one is displayed, so
 * switching to a preset keeps the custom message for later.
 */
export function statusActions() {
  return {
    chatStatusSaveError: false,
    _chatStatusSaveErrorTimer: null,
    chatStatusAnnouncement: '',

    // Inline "Update Status" form state
    chatStatusFormOpen: false,
    chatStatusDraft: '',
    chatStatusCustomMaxLength: CHAT_STATUS_CUSTOM_MAX_LENGTH,

    toggleChatStatusPicker() {
      this.dialog = this.dialog === DIALOGS.CHAT_STATUS ? null : DIALOGS.CHAT_STATUS;
      if (this.dialog !== DIALOGS.CHAT_STATUS) {
        this.chatStatusFormOpen = false;
      }
    },

    openChatStatusPicker() {
      this.dialog = DIALOGS.CHAT_STATUS;
    },

    closeChatStatusPicker() {
      if (this.dialog === DIALOGS.CHAT_STATUS) {
        this.dialog = null;
      }
      this.chatStatusFormOpen = false;
    },

    openChatStatusForm() {
      this.chatStatusDraft = this.chatStatus.customMessage || '';
      this.chatStatusFormOpen = true;
    },

    closeChatStatusForm() {
      this.chatStatusFormOpen = false;
    },

    async saveChatStatusCustomMessage() {
      const customMessage = this.chatStatusDraft.trim();
      if (!customMessage) {
        return;
      }

      const previous = this.chatStatus;

      // Optimistic: reflect the message and close immediately; revert on error.
      this.chatStatus = {
        ...previous,
        label: customMessage,
        customMessage,
        customMessageActive: true,
      };
      this.closeChatStatusPicker();

      try {
        await this.$api({
          url: '/chat/status',
          method: 'PATCH',
          data: { custom_message: customMessage },
        });
        this.trackChat('status', 'set', 'custom');
        this.announceChatStatusChanged(customMessage);
      } catch (err) {
        this.chatStatus = previous;
        this.showChatStatusSaveError();
        Sentry.captureException(err, {
          extra: {
            message: 'Failed to update custom chat status',
          },
        });
      }
    },

    // Re-display an already-stored custom message (clicking the custom row)
    // without changing its text — resends it so the backend reactivates it.
    async activateChatStatusCustomMessage() {
      const previous = this.chatStatus;
      const customMessage = previous.customMessage;
      if (!customMessage || previous.customMessageActive) {
        this.closeChatStatusPicker();
        return;
      }

      this.chatStatus = { ...previous, label: customMessage, customMessageActive: true };
      this.closeChatStatusPicker();

      try {
        await this.$api({
          url: '/chat/status',
          method: 'PATCH',
          data: { custom_message: customMessage },
        });
        this.trackChat('status', 'set', 'custom');
        this.announceChatStatusChanged(customMessage);
      } catch (err) {
        this.chatStatus = previous;
        this.showChatStatusSaveError();
        Sentry.captureException(err, {
          extra: {
            message: 'Failed to reactivate custom chat status',
          },
        });
      }
    },

    async selectChatStatusPreset(preset, label) {
      const previous = this.chatStatus;

      // Optimistic: reflect the choice and close immediately; revert on error.
      // Picking a preset keeps any custom message but stops displaying it.
      this.chatStatus = { ...previous, preset, label, customMessageActive: false };
      this.closeChatStatusPicker();

      if (preset === previous.preset && !previous.customMessageActive) {
        return;
      }

      try {
        await this.$api({
          url: '/chat/status',
          method: 'PATCH',
          data: { preset },
        });
        this.trackChat('status', 'set', preset);
        this.announceChatStatusChanged(label);
      } catch (err) {
        this.chatStatus = previous;
        this.showChatStatusSaveError();
        Sentry.captureException(err, {
          extra: {
            message: 'Failed to update chat status',
            preset,
          },
        });
      }
    },

    announceChatStatusChanged(status) {
      this.chatStatusAnnouncement = '';
      this.$nextTick(() => {
        this.chatStatusAnnouncement = i18n.t('chat.chat_status.changed_announcement', { status });
      });
    },

    showChatStatusSaveError() {
      this.chatStatusSaveError = true;
      clearTimeout(this._chatStatusSaveErrorTimer);
      this._chatStatusSaveErrorTimer = setTimeout(() => {
        this.chatStatusSaveError = false;
      }, 10000);
    },
  };
}
